import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { DisablerAction, type DisablerDevice, type DisablerEvent, type ESN } from '@cobliteam/common-types';

import { DisablerRequester, type WebData, isWebDataSuccess } from '@cobliteam/api-lib';

import { Environment } from '@cobliteam/react-environments';

import { askDisablerActionFailed, askDisablerActionSucceeded, loadDevicesStatusFailed, loadDevicesStatusSucceeded } from '@cobliteam/store/src/Disabler/actionCreators';
import { DisablerActionTypes } from '@cobliteam/store/src/Disabler/actionTypes';
import type { AskDisablerActionRequest } from '@cobliteam/store/src/Disabler/actions';
import { selectDisablerDevices } from '@cobliteam/store/src/Disabler/selectors';

import Alert from '@cobliteam/ui/components/Alert';

const env = Environment.getInstance().getEnvironment();
const disablerRequester = new DisablerRequester({ host: env.herbieApiHost });

export function* loadDevicesStatusSaga() {
  try {
    const devicesStatus: DisablerEvent[] = yield call(disablerRequester.getFleetDevicesStatus);
    yield put(loadDevicesStatusSucceeded(devicesStatus));
  } catch (err) {
    Alert.error('Houve um erro, tente novamente mais tarde.');
    yield put(loadDevicesStatusFailed());
  }
}

const getRequesterMethod = (action: DisablerAction) => {
  if (action === DisablerAction.Block || action === DisablerAction.CancelUnblock) {
    return disablerRequester.requestDisable;
  }
  return disablerRequester.requestEnable;
};

const isRequestRepeated = (deviceId: ESN, action: DisablerAction, currentDevicesWebData: WebData<DisablerDevice[]>) => {
  const currentDevices = isWebDataSuccess(currentDevicesWebData) ? currentDevicesWebData.data : [];
  const requestedAction = currentDevices.find((device) => device.deviceId === deviceId);

  return !!requestedAction && requestedAction.action === action;
};

export function* requestDisablerActionSaga(request: AskDisablerActionRequest) {
  const { deviceId, requestedAction } = request.payload;
  const requesterMethod = getRequesterMethod(requestedAction);

  try {
    const currentDevicesWebData: WebData<DisablerDevice[]> = yield select(selectDisablerDevices);
    const isRepeated: boolean = yield call(isRequestRepeated, deviceId, requestedAction, currentDevicesWebData);

    if (isRepeated) {
      const message =
        requestedAction === DisablerAction.Block || requestedAction === DisablerAction.CancelUnblock
          ? 'O bloqueio deste veículo já está em andamento!'
          : 'O desbloqueio deste veículo já está em andamento!';

      Alert.warning(message);
    } else {
      yield call(requesterMethod, deviceId);
      yield put(askDisablerActionSucceeded(deviceId, requestedAction));
    }
  } catch (err) {
    const message =
      requestedAction === DisablerAction.Block || requestedAction === DisablerAction.CancelUnblock
        ? 'Ops! O sistema apresentou um erro e não foi possível iniciar o bloqueio, tente novamente.'
        : 'Ops! O sistema apresentou um erro e não foi possível iniciar o desbloqueio, tente novamente.';

    Alert.error(message);
    yield put(askDisablerActionFailed());
  }
}

export function* DisablerSagasListener() {
  yield all([
    takeLatest(DisablerActionTypes.LOAD_DEVICES_STATUS_REQUEST, loadDevicesStatusSaga),
    takeLatest(DisablerActionTypes.ASK_DISABLER_ACTION_REQUEST, requestDisablerActionSaga),
  ]);
}
